<template>
  <AppLoader />
  <AppNavbar v-if="!shouldHideNavbar" />
  <router-view />
  <AppFooter v-if="!shouldHideFooter" />
  <CookieConsent />
  <WelcomePopup />
  <PickModeBar />
</template>
<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import AppNavbar from '@/app/components/AppNavbar.vue'
import AppFooter from '@/app/components/AppFooter.vue'
import PickModeBar from '@/modules/admin/ui/PickModeBar.vue'
import { initPickMode, isPickMode, exitPickMode } from '@/composables/usePickMode'
import AppLoader from '@/app/components/AppLoader.vue'
import CookieConsent from '@/app/components/CookieConsent.vue'
import WelcomePopup from '@/modules/welcome/components/WelcomePopup.vue'
import { useStore } from 'vuex'
import { useMaintenanceMonitor } from '@/utils/maintenanceMonitor'
import { loadSiteContactPublic, refreshSiteContactPublic, siteContactPublicRef } from '@/composables/useSiteContact'
import { useI18n } from '@/composables/useI18n'
import { applyStorefrontThemeFromConfig } from '@/composables/useStorefrontTheme'
import { applySeoMeta } from '@/utils/seoMeta'
import { resolveRouteTitle } from '@/utils/siteTitle'

const route = useRoute()
const store = useStore()
const { locale } = useI18n()
const isFooterVisible = ref(false)

let footerObserver = null
let handleScrollOrResize = null

// Check if current route is an admin route or account page when user is admin
const shouldHideNavbar = computed(() => {
  // Ocultar navbar/footer en rutas de cuenta y panel
  if (route.path.startsWith('/account')) {
    return true
  }
  if (route.path.startsWith('/configuracion')) {
    return true
  }
  if (route.path.startsWith('/mantenimiento')) {
    return true
  }
  if (route.path.startsWith('/carrito')) {
    return true
  }
  if (
    route.name === 'LoginForm' ||
    route.name === 'RegisterForm' ||
    route.name === 'EmailVerification' ||
    route.path === '/recuperar-contrasena'
  ) {
    return true
  }
  return false
})

const shouldHideFooter = computed(() => {
  if (route.path === '/') {
    return true
  }
  if (route.meta?.embeddedFooter) {
    return true
  }
  if (route.path.startsWith('/account')) {
    return true
  }
  if (route.path.startsWith('/configuracion')) {
    return true
  }
  if (route.path.startsWith('/mantenimiento')) {
    return true
  }
  return false
})

const pageShellTone = computed(() => {
  // Regla: oscuro cuando la navbar oscura está visible, claro cuando no.
  // (Antes se usaba shouldHideFooter como proxy: el home no tiene footer y
  // quedaba light a pesar de tener la navbar + gradient.)
  if (shouldHideNavbar.value) {
    return 'light'
  }
  return 'dark'
})

function syncPageShellTone() {
  document.body.classList.toggle('page-shell--dark', pageShellTone.value === 'dark')
  document.body.classList.toggle('page-shell--light', pageShellTone.value === 'light')
  // Fallback inline para el primer paint: en dev el CSS de la app puede tardar
  // en inyectarse, el inline style garantiza el tono desde el primer frame.
  document.body.style.backgroundColor = pageShellTone.value === 'dark' ? '#0d0d0d' : ''
}

const setupFooterObserver = () => {
  if (footerObserver) {
    footerObserver.disconnect()
    footerObserver = null
  }

  if (handleScrollOrResize) {
    window.removeEventListener('scroll', handleScrollOrResize)
    window.removeEventListener('resize', handleScrollOrResize)
    handleScrollOrResize = null
  }

  const footerEl = document.querySelector('footer')

  if (!footerEl) {
    isFooterVisible.value = false
    return
  }

  if ('IntersectionObserver' in window) {
    footerObserver = new IntersectionObserver((entries) => {
      const entry = entries[0]
      isFooterVisible.value = Boolean(entry && entry.isIntersecting && entry.intersectionRatio > 0)
    }, {
      root: null,
      threshold: [0, 0.1]
    })
    footerObserver.observe(footerEl)
    return
  }

  const update = () => {
    const rect = footerEl.getBoundingClientRect()
    isFooterVisible.value = rect.top < window.innerHeight && rect.bottom > 0
  }

  update()
  handleScrollOrResize = () => update()
  window.addEventListener('scroll', handleScrollOrResize, { passive: true })
  window.addEventListener('resize', handleScrollOrResize)
}

// Watch for route changes to recheck conditions
watch(() => route.path, async () => {
  applySeoMeta(resolveRouteTitle(route.meta))
  syncPageShellTone()
  await nextTick()
  setupFooterObserver()
})

watch(pageShellTone, syncPageShellTone)

// Al cambiar el idioma, re-fetch del config público: el backend resuelve
// navbar_links/footer_links/faqs/... con ?lang=, pero el singleton cachea el
// primer fetch — sin este refresh los navlinks quedaban en el idioma original.
watch(locale, () => {
  refreshSiteContactPublic()
})

watch(
  siteContactPublicRef,
  (config) => {
    if (config === undefined) return;
    applyStorefrontThemeFromConfig(config);
  },
)

// Si el modo selección está activo y se navega lejos de la home (logo, carrito…),
// se sale para que no quede la barra inferior flotando en otra página.
watch(
  () => route.path,
  () => {
    if (isPickMode.value && route.path !== '/') exitPickMode();
  },
)

onMounted(() => {
  loadSiteContactPublic().then(() => {
    applySeoMeta(resolveRouteTitle(route.meta))
  })

  initPickMode()

  const deferCartHydrate = window.requestIdleCallback
    ? (cb) => window.requestIdleCallback(cb, { timeout: 2000 })
    : (cb) => setTimeout(cb, 150)
  deferCartHydrate(() => {
    store.dispatch('hydrateCartFromServer').catch(() => undefined)
  })

  syncPageShellTone()
  nextTick(() => setupFooterObserver())
  
  // Inicializar verificación de mantenimiento con TTL más largo (5 minutos)
  // para reducir consultas al servidor
  useMaintenanceMonitor(5) // Check every 5 minutes (aumentado de 2 a 5 minutos)

})

onBeforeUnmount(() => {
  document.body.classList.remove('page-shell--dark', 'page-shell--light')
  document.body.style.backgroundColor = ''
  if (footerObserver) {
    footerObserver.disconnect()
    footerObserver = null
  }
  if (handleScrollOrResize) {
    window.removeEventListener('scroll', handleScrollOrResize)
    window.removeEventListener('resize', handleScrollOrResize)
  }
})
</script>

<style>
  @import '@/assets/css/variables.css';

:root {
  --store-surface-dark: #0d0d0d;
}
@font-face {
  font-family: 'General-Sans';
  src: url('@/assets/fonts/GeneralSans-Variable.ttf') format('truetype');
  font-display: swap;
}
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: 'General-Sans', sans-serif;
  scroll-behavior: smooth;
}

body {
  background-color: var(--bg-primary);
  color: var(--text-primary);
}

body.page-shell--dark {
  background-color: var(--store-surface-dark);
}

body.page-shell--light {
  background-color: var(--bg-primary);
}

.Vue-Toastification__toast,
.Vue-Toastification__toast--success,
.Vue-Toastification__toast--error,
.Vue-Toastification__toast--warning,
.Vue-Toastification__toast--info,
.Vue-Toastification__toast--default {
  --toast-accent: #0d6efd;
  background: #ffffff !important;
  color: #141414 !important;
  border: 1px solid rgba(17, 24, 39, 0.06) !important;
  border-radius: 6px !important;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.08) !important;
  font-family: 'General-Sans', sans-serif !important;
  min-width: 300px !important;
  max-width: min(360px, calc(100vw - 24px)) !important;
  min-height: 64px !important;
  padding: 17px 18px 20px 18px !important;
  align-items: center !important;
  gap: 10px !important;
  overflow: hidden !important;
}

.Vue-Toastification__toast--success {
  --toast-accent: #2ebf61;
}

.Vue-Toastification__toast--info,
.Vue-Toastification__toast--default {
  --toast-accent: #0d7ee7;
}

.Vue-Toastification__toast--warning {
  --toast-accent: #f7a400;
}

.Vue-Toastification__toast--error {
  --toast-accent: #ef412f;
}

.Vue-Toastification__toast-body {
  color: #141414 !important;
  font-size: 14px !important;
  font-weight: 650;
  line-height: 1.35 !important;
  padding-right: 4px;
  white-space: normal !important;
}

.Vue-Toastification__icon {
  color: var(--toast-accent) !important;
  flex: 0 0 22px !important;
  width: 22px !important;
  height: 22px !important;
  margin: 0 !important;
}

.Vue-Toastification__icon svg {
  display: block;
  width: 22px !important;
  height: 22px !important;
}

.Vue-Toastification__icon.godea-toast-icon {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: var(--toast-accent) !important;
  color: #ffffff !important;
  border-radius: 50% !important;
  font-style: normal !important;
  font-size: 14px !important;
  font-weight: 750 !important;
  line-height: 1 !important;
}

.godea-toast-icon::before {
  content: "!";
}

.godea-toast-icon--success::before {
  content: "\2713";
  transform: translateY(-0.5px);
}

.godea-toast-icon--info::before {
  content: "i";
  font-family: Arial, sans-serif;
  font-size: 15px;
  font-weight: 700;
}

.Vue-Toastification__progress-bar {
  background: var(--toast-accent) !important;
  height: 4px !important;
  opacity: 1 !important;
}

.Vue-Toastification__close-button {
  color: #2f3337 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 24px !important;
  height: 24px !important;
  margin-left: auto !important;
  padding: 0 !important;
  border-radius: 50% !important;
  font-size: 24px !important;
  font-weight: 300 !important;
  line-height: 1 !important;
  opacity: 1 !important;
}

.Vue-Toastification__close-button:hover,
.Vue-Toastification__close-button:focus {
  background: rgba(15, 23, 42, 0.06) !important;
}

@media only screen and (max-width: 600px) {
  .Vue-Toastification__container {
    width: 100vw !important;
    padding: 12px !important;
  }

  .Vue-Toastification__container .Vue-Toastification__toast {
    width: 100% !important;
    min-width: 0 !important;
    margin-bottom: 10px !important;
  }
}

.container {
  max-width: var(--container-max, 1300px);
}

.bg-dark {
  background-color: var(--black-900) !important;
}

.border-primary {
  border-color: var(--primary-color) !important;
}

.display-2, .display-3, .display-4, .display-5 {
  font-weight: 800;
}

.text-white {
  color: #ffffff !important;
}

.text-gradient-primary {
  background: linear-gradient(160deg, var(--primary-color), var(--primary-dark));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* settings-section (panel /configuracion) usa position:sticky; overflow:hidden lo rompe */
section:not(.settings-section) {
  position: relative;
  overflow: hidden;
}

/* product detail / cart checkout: sticky sidebar needs visible overflow on ancestors */
.pd-page,
.cart-section,
.form-section {
  overflow: visible;
}

.settings-section {
  position: relative;
  overflow: visible;
}

.h5 {
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--text-primary);
}

.fs-6 {
  font-size: .8rem;
}

.fs-7 {
  font-size: 0.875rem !important;
}

.btn {
  font-size: .85rem !important;
  padding: 0.5rem 1rem;
  border-radius: var(--btn-radius);
  transition: all var(--transition-base);
}

.btn:hover {
  color: inherit;
}

.hover-opacity {
  transition: 0.3s ease;
}

.hover-opacity:hover {
  opacity: 0.8;
}

/* badges */
.status-badge {
  display: inline-flex;
  padding: 0.4rem 0.8rem;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}

.btn-outline-primary {
  background-color: transparent;
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  cursor: pointer;
  transition: all var(--transition-base);
}

.btn-outline-primary.active,
.btn-outline-primary:hover {
  background-color: var(--primary-color);
  color: var(--text-on-primary);
  border-color: var(--primary-color);
}

.text-primary {
  color: var(--primary-color) !important;
}

.bg-primary {
  background-color: var(--primary-color) !important;
}

.text-dark {
  color: var(--text-primary) !important;
}

.btn-primary {
  background-color: var(--btn-primary-bg) !important;
  color: var(--btn-primary-text) !important;
  border: none !important;
  padding: .5rem 1.3rem;
}

.btn-primary:hover {
  background-color: var(--btn-primary-hover) !important;
}

button, a {
  font-weight: 400;
  font-size: 1rem;
}

h1, h2, h3, h4, h5, h6 {
  font-weight: 700;
  color: var(--text-primary);
}

h1 { font-size: 2rem; }
h2 { font-size: 1.7rem; }
h3 { font-size: 1.5rem; font-weight: 600; letter-spacing: 0.5px; }
h4 { font-size: 1.25rem; font-weight: 600; }
h5 { font-size: 1rem; font-weight: 500; }

p {
  font-size: 1rem;
  color: var(--text-secondary);
  font-weight: 400;
  line-height: 1.6;
}

b { font-weight: 700; }

@media (max-width: 525px) {
  .display-2 { font-size: 3.5rem !important; }
}

@media (max-width: 400px) {
  .display-2 { font-size: 3rem !important; }
}

/* WhatsApp Floating Button Styles */
.whatsapp-float {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #25d366;
  color: #ffffff;
  border-radius: 50px;
  text-align: center;
  font-size: 30px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.3);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  transition: all 0.3s;
}

.whatsapp-float:hover {
  background-color: #20ba5a;
  color: white;
}

.whatsapp-icon {
  margin-top: 4px;
}

.whatsapp-tooltip {
  position: absolute;
  right: 60px;
  background-color: #333;
  color: white;
  border-radius: 5px;
  padding: 8px 15px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  visibility: hidden;
  opacity: 0;
  transition: all 0.3s;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
}

.whatsapp-tooltip::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 100%;
  margin-top: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent transparent #333;
}

.whatsapp-float:hover .whatsapp-tooltip {
  visibility: visible;
  opacity: 1;
}

@media screen and (max-width: 768px) {
  .whatsapp-float {
    width: 50px;
    height: 50px;
    font-size: 25px;
    bottom: 15px;
    right: 15px;
  }
  
  .whatsapp-tooltip {
    display: none;
  }
}
</style>
